// Class List: imports and dependencies
import React, { useState, useEffect } from "react";
import { PageLoader } from "../components/Loader/Loader";
import {
  AddClassIcon,
  SubjectsIcon,
  TimeTableIcon,
} from "../components/Icons/Icons";
import "../components/Icons/CreateIconButton.css";
import { API_BASE_URL } from "../config";
import { useToastMessageState } from "../components/Toast/Toast";
import AddClass from "./AddClass";
import EditClass from "./EditClass";
import AssignSubjects from "./AssignSubjects";
import TimeTable from "./TimeTable";
import Pagination from "./Pagination";
import "./ClassList.css";
import { usePermissions } from "../security/Permissions";

// Data types and contracts
interface Subject {
  subjectId: number;
  subjectName: string;
}

interface Section {
  id: number;
  sectionName: string;
  staffId: number;
  monitorStudentId: number;
  subjects: Subject[];
}

interface Class {
  id: number;
  className: string;
  schoolId: number;
  createdDate: string;
  isActive: boolean;
  sectionCount: number;
  sections: Section[];
}

interface ClassListProps {
  selectedSchoolId: number | null;
}

// Main component and state
const ClassList: React.FC<ClassListProps> = ({
  selectedSchoolId,
  onNavigate,
}) => {
  const { can } = usePermissions();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useToastMessageState("error");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageInput, setPageInput] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignSubjectsOpen, setIsAssignSubjectsOpen] = useState(false);
  const [isTimeTableOpen, setIsTimeTableOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedSection, setSelectedSection] = useState<{
    id: number;
    name: string;
    subjects: Subject[];
  } | null>(null);

  useEffect(() => {
    if (selectedSchoolId) {
      setCurrentPage(1);
      fetchClasses(1);
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    if (selectedSchoolId && currentPage > 1) {
      fetchClasses(currentPage);
    }
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchClasses(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchClasses(1, size);
  };

  const fetchClasses = async (page: number = 1, size: number = pageSize) => {
    if (!selectedSchoolId) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/Class/calss-list?schoolId=${selectedSchoolId}&page=${page}&pageSize=${size}`,
        {
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setClasses(result.data);
          setCurrentPage(result.currentPage);
          setTotalPages(result.totalPages);
          setTotalRecords(result.totalRecords);
        } else {
          setError(result.message || "Failed to fetch classes");
        }
      } else {
        setError("Failed to fetch classes");
      }
    } catch (err) {
      setError("Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading classes..." />;
  }

  if (error) {
    return <div className="staff-list-loading">Error: {error}</div>;
  }

  if (!selectedSchoolId) {
    return <div className="staff-list-loading">Please select a school</div>;
  }

  return (
    <div className="staff-list-container">
      <div className="staff-list-header">
        <h2>Class List</h2>
        {can("academics.classes", "create") && (
          <button
            type="button"
            className="create-icon-button"
            title="Add Class"
            aria-label="Add Class"
            onClick={() => setIsAddModalOpen(true)}
          >
            <AddClassIcon size={26} />
          </button>
        )}
      </div>

      {classes.length === 0 ? (
        <div
          className="staff-list-loading"
          style={{ textAlign: "center", padding: "40px", color: "#666" }}
        >
          No classes available. Please add a new class.
        </div>
      ) : (
        <div className="staff-table-wrapper">
          <table className="staff-table">
            <thead>
              <tr>
                <th>Class Name</th>
                <th>Sections</th>
                <th>Subjects</th>
                <th>Time Table</th>
                <th>Status</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((classItem) => (
                <tr key={classItem.id}>
                  <td className="class-name">
                    <span
                      className="staff-name-link"
                      onClick={() => {
                        if (!can("academics.classes", "update")) return;
                        setSelectedClass(classItem);
                        setIsEditModalOpen(true);
                      }}
                    >
                      {classItem.className}
                    </span>
                  </td>
                  <td>
                    <div className="sections-list">
                      {classItem.sections.map((section) => (
                        <span key={section.id} className="section-badge">
                          {section.sectionName}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="create-icon-button"
                      title="Subjects"
                      aria-label={`Subjects for ${classItem.className}`}
                      onClick={() => {
                        if (!can("academics.classes", "update")) return;
                        setSelectedClass(classItem);
                        setIsAssignSubjectsOpen(true);
                      }}
                    >
                      <SubjectsIcon size={26} />
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="create-icon-button"
                      title="Time Table"
                      aria-label={`Time Table for ${classItem.className}`}
                      onClick={() => {
                        setSelectedClass(classItem);
                        setIsTimeTableOpen(true);
                      }}
                    >
                      <TimeTableIcon size={26} />
                    </button>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${classItem.isActive ? "active" : "inactive"}`}
                    >
                      {classItem.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="created-date">
                    {new Date(classItem.createdDate).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[5, 10, 20, 50]}
      />

      <AddClass
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        schoolId={selectedSchoolId}
        onSuccess={() => fetchClasses(currentPage)}
      />

      <EditClass
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        classData={selectedClass}
        onSuccess={() => fetchClasses(currentPage)}
      />

      <AssignSubjects
        isOpen={isAssignSubjectsOpen}
        onClose={() => setIsAssignSubjectsOpen(false)}
        classData={selectedClass}
        schoolId={selectedSchoolId}
        onNavigateToSubjects={() => {
          setIsAssignSubjectsOpen(false);
          onNavigate?.("Subject List");
        }}
        onSuccess={() => {
          fetchClasses(currentPage);
          console.log("Subjects assigned successfully");
        }}
      />

      <TimeTable
        isOpen={isTimeTableOpen}
        onClose={() => setIsTimeTableOpen(false)}
        classData={selectedClass}
        schoolId={selectedSchoolId}
        onSuccess={() => {
          console.log("TimeTable created successfully");
        }}
      />
    </div>
  );
};

export default ClassList;
